import { request } from '@/helpers/apiClient';
import { useQuery } from '@tanstack/react-query';
import { HTTP_METHODS } from '@/utils/constants/httpMethods';
import { FeatureConfig } from '@/utils/constants/featuresFlag';
import { convertValuesInObject } from '@/helpers/convertValuesInObject';
import { formatDateParam } from '@/helpers/formatDateParam';
import { buildDateQueryString } from '@/helpers/buildDateQueryString';

const customerApiUrl = 'api/v1/adminui/customer';
const customersApiUrl = 'api/v1/adminui/customers';

/**
 * Builds query string from filter object, excluding empty/null/undefined values
 * @param {Object} filters - Filter parameters to apply to the query
 * @returns {string} Query string starting with '&' if filters exist, empty string otherwise
 */
const buildFilterQueryString = (filters = {}) => {
  if (Object.keys(filters).length === 0) return '';

  const queryParams = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  }
  const queryString = queryParams.toString();
  return queryString ? `&${queryString}` : '';
};

/**
 * API configuration for customer-related endpoints
 */
export const CUSTOMER_CONFIG = {
  GET_CUSTOMER_BY_ID: customerId => ({
    method: HTTP_METHODS.GET,
    endPoint: `${customerApiUrl}/${customerId}`,

    baseUrlKey: 'cepApiUrl',
    headers: {
      'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
      siteId: import.meta.env[FeatureConfig.cepSiteId]
    }
  }),

  GET_CUSTOMER_LIST: (page = 0, pageSize = 10, filters = {}) => {
    let url = `${customersApiUrl}?page=${page}&pageSize=${pageSize}`;

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: {
        'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
        siteId: import.meta.env[FeatureConfig.cepSiteId]
      }
    };
  }
};

/**
 * Fetches customer data by ID from the CEP API
 */
export const fetchCustomerById = async customerId => {
  const response = await request(
    CUSTOMER_CONFIG.GET_CUSTOMER_BY_ID(customerId)
  );
  return response;
};

/**
 * Fetches paginated customer list from the CEP API
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {Promise} - Promise with customer list data
 */
export const fetchCustomerList = async (
  page = 0,
  pageSize = 10,
  filters = {}
) => {
  return request(CUSTOMER_CONFIG.GET_CUSTOMER_LIST(page, pageSize, filters));
};

/**
 * React Query hook for retrieving and caching customer data by ID
 */
export const useCustomerData = customerId => {
  return useQuery({
    queryKey: ['customerData', customerId],
    queryFn: () => fetchCustomerById(customerId),
    enabled: !!customerId
  });
};

/**
 * React Query hook for retrieving and caching paginated customer list
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters to apply to the query
 * @param {Date | null} startDate - Start date filter
 * @param {Date | null} endDate - End date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useCustomerList = (page = 0, pageSize = 10, filters = {}) => {
  const filterKey = JSON.stringify(filters);
  return useQuery({
    queryKey: ['customerList', page, pageSize, filters, filterKey],
    queryFn: () => fetchCustomerList(page, pageSize, filters),
    keepPreviousData: true
  });
};
